import type { SkillTest, SkillTestDraft } from '../types/skillTests';
import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

const normalizeTest = (test: SkillTest) => ({
  ...test,
  id: Number(test.id) || Date.now(),
  questions: test.questions?.map((question, index) => ({
    ...question,
    id: typeof question.id === 'number' ? question.id : index + 1,
  })),
});

export async function fetchSkillTests(): Promise<SkillTest[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load skill tests.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/tests`);
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Skill test fetch failed (${response.status}): ${errorText}`);
  }
  const payload = await response.json();
  if (Array.isArray(payload?.tests)) {
    return payload.tests.map(normalizeTest);
  }
  return [];
}

export async function createSkillTestApi(draft: SkillTestDraft): Promise<SkillTest> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to create skill tests.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/tests`, {
    method: 'POST',
    body: JSON.stringify(draft),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Skill test create failed (${response.status}): ${errorText}`);
  }
  return normalizeTest(await response.json());
}

export async function updateSkillTestApi(id: string, draft: SkillTestDraft): Promise<SkillTest> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update skill tests.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/tests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(draft),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Skill test update failed (${response.status}): ${errorText}`);
  }
  return normalizeTest(await response.json());
}

export async function deleteSkillTestApi(id: string): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to delete skill tests.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/tests/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Skill test delete failed (${response.status}): ${errorText}`);
  }
}
